// Pieces the goods-receipt screens share (Sprint 4; UC-PURCHASING-005, -008, UC-PRICING-007): a receipt's number as a link for the
// roles that read receipts (`purchasing.po.read`) and as plain text for the rest (UC-PURCHASING-008 E2); the discount PIN thresholds
// as they resolve at the acting branch and the floor below which a kept price needs a Control PIN (BR-PRICING-017) — worked with
// the money package's `applyPercent`, as pricing works it; the words of a proposal as the screen makes it (a band with its range, a
// product markup) and as a receipt froze it (UC-PURCHASING-008 step 4); and a receipt as posted — its details and its lines with
// their serials and prices — shown by the receipt's record and by Receive stock once it posts. Nothing here invents a class.
import { bp, cents, type GoodsReceiptLineView, type GoodsReceiptView, type GoodsReceiptWarning, type ReceiptProposal, type SuggestResult } from "@visionx/contracts";
import { applyPercent } from "@visionx/money";
import { useQuery } from "@tanstack/react-query";
import { Fragment, type ReactNode } from "react";
import { Link } from "react-router";
import { effectiveSettings } from "../api/endpoints.catalogue";
import { formatDate, formatDateTime, formatMoney } from "../lib/format";
import { can, useSessionView } from "../session/store";
import { Alert, Chip, KV, Panel } from "../ui/bits";
import { markupText, possessive, rangeText } from "./PricingShared";
import { ControlLogRef, shortName } from "./StockShared";

const dash = <span className="faint">—</span>;

/** A calendar day `YYYY-MM-DD` as the screens print it (midday, so no timezone moves the day). */
export const dayWords = (day: string | null | undefined): string => (day ? formatDate(`${day.slice(0, 10)}T12:00:00.000Z`) : "—");

/** A receipt's number: a link to it for the roles that read receipts, else its number alone (UC-PURCHASING-008 A3, E2). */
export function ReceiptLink({ id, number }: { id: string | null | undefined; number: string | null | undefined }) {
  const s = useSessionView();
  if (!number) return null;
  return id && can(s, "purchasing.po.read") ? <Link className="mono" to={`/purchasing/receipts/${id}`}>{number}</Link> : <span className="mono">{number}</span>;
}

// --- the floor (BR-PRICING-017) -------------------------------------------------------------------------------------------

export interface Thresholds { percentBp: number; cents: number }

/**
 * The organisation's discount PIN thresholds as they resolve at the acting branch (`sales.discountPinThresholdPercentBp`,
 * `sales.discountPinThresholdCents`; UC-TENANCY-007), read with every other setting in force; the session's values until then.
 */
export function useDiscountThresholds(): Thresholds {
  const s = useSessionView();
  const effective = useQuery({ queryKey: ["settings", "effective"], queryFn: () => effectiveSettings.get(), staleTime: 60_000 });
  const sales = effective.data?.data.settings.sales;
  const pct = sales?.discountPinThresholdPercentBp?.value;
  const rands = sales?.discountPinThresholdCents?.value;
  return {
    percentBp: typeof pct === "number" ? pct : s?.settings.discountPinThresholdPercentBp ?? 0,
    cents: typeof rands === "number" ? rands : s?.settings.discountPinThresholdCents ?? 0,
  };
}

/** The floor under the price in force: that price less the smaller of its percentage (half-up) and the rand amount; none without a price. */
export function floorOf(current: number | null, t: Thresholds): number | null {
  if (current === null) return null;
  return current - Math.min(applyPercent(cents(current), bp(t.percentBp)), t.cents);
}

/** Whether a kept price needs the Control PIN: lower than the price in force and below its floor. Raising one never does. */
export function belowFloor(current: number | null, kept: number | null, t: Thresholds): boolean {
  if (current === null || kept === null || kept >= current) return false;
  return kept < floorOf(current, t)!;
}

// --- a proposal in words (BR-PURCHASING-017, UC-PRICING-007) ----------------------------------------------------------------

/**
 * The proposal as the screen makes it: `300% band (R 500.00–R 1 000.00)`, `Menlyn's 60% band (R 3 000.00 and above)`,
 * `markup 100%`, `Menlyn's markup 180%`.
 */
export function suggestionBasis(r: SuggestResult, branchName: string): string | null {
  const p = r.proposal;
  if (!p) return null;
  if (p.basis === "FrameMarkupBand" && p.band) {
    const own = p.band.scope === "Branch" ? `${possessive(branchName)} ` : "";
    return `${own}${markupText(p.band.marginPercentBp, p.band.marginValueCents)} band (${rangeText(p.band)})`;
  }
  if (p.basis === "ProductMarkup" && p.productMarkup) {
    const own = p.productMarkup.scope === "Branch" ? `${possessive(branchName)} ` : "";
    return `${own}markup ${markupText(p.productMarkup.percentBp, p.productMarkup.valueCents)}`;
  }
  return markupText(p.percentBp, p.valueCents);
}

/** The proposal a line froze: `300% band → R 2 480.00`, `markup 100% → R 640.00`, `no band or markup`; nothing while proposals were off. */
export function frozenProposalWords(p: ReceiptProposal | null): string {
  if (!p) return "nothing proposed — the branch proposes no price at receipt";
  if (p.source === "None" || p.proposedCents === null) return "no band or markup";
  const pct = p.marginPercentBp === null ? "" : markupText(p.marginPercentBp, p.marginValueCents ?? 0);
  return `${p.source === "Band" ? `${pct} band` : `markup ${pct}`} → ${money(p.proposedCents)}`;
}

/** An amount inside a sentence that may wrap: `R 2 480.00` never breaks across lines. */
const money = (c: number | null | undefined): string => formatMoney(c).replace(/ /g, "\u00a0");

/** `the unit`, `both units`, `all 3 units`. */
const unitsWord = (n: number): string => (n === 1 ? "the unit" : n === 2 ? "both units" : `all ${n} units`);

/** A posted line's price, as the receipt froze it (UC-PURCHASING-008 step 4). */
export function postedPriceWords(l: GoodsReceiptLineView, branchName: string, postedAt: string): ReactNode {
  const parts: string[] = [frozenProposalWords(l.proposal)];
  if (l.proposal) parts.push(l.proposal.currentCents === null ? "no current price" : `current ${money(l.proposal.currentCents)}`);
  if (l.units.length > 0) {
    const groups = new Map<number | null, number>();
    for (const u of l.units) groups.set(u.keptPriceCents, (groups.get(u.keptPriceCents) ?? 0) + 1);
    const kept = [...groups.entries()];
    if (kept.length === 1) parts.push(kept[0]![0] === null ? "no price kept" : `kept ${money(kept[0]![0])} on ${unitsWord(l.units.length)}`);
    else parts.push(`kept ${kept.map(([c, n], i) => `${c === null ? "no price" : money(c)} on ${n}${i === 0 ? ` unit${n === 1 ? "" : "s"}` : ""}`).join(", ")}`);
  } else parts.push(l.keptPriceCents === null ? "no price kept" : `kept ${money(l.keptPriceCents)}`);
  const recorded: string[] = [];
  if (l.tenantPriceCents !== null) recorded.push(`${money(l.tenantPriceCents)} became the product's price — it had none`);
  if (l.branchPriceId) recorded.push(`set ${possessive(branchName)} price from ${formatDate(postedAt)}`);
  const own = l.units.filter((u) => u.ownPriceCents !== null).length;
  if (own > 0) recorded.push(`${own === 1 ? "one unit sells" : `${own} units sell`} at its own price`);
  return <>{parts.join(" · ")}{recorded.length > 0 && <div className="small">{recorded.join(" · ")}</div>}</>;
}

/** The reasons a line's prices carry, with who kept each (BR-PURCHASING-017: each unit freezes its reason and who kept it). */
function priceReasons(l: GoodsReceiptLineView): ReactNode[] {
  const out: ReactNode[] = [];
  if (l.units.length === 0) {
    if (l.priceReason) out.push(<>“{l.priceReason}”{l.keptBy ? ` · ${shortName(l.keptBy.displayName)}` : ""}</>);
    return out;
  }
  const seen = new Set<string>();
  for (const u of l.units) {
    if (!u.priceReason) continue;
    const key = `${u.priceReason}|${u.keptPriceCents}`;
    const same = l.units.filter((x) => x.priceReason === u.priceReason && x.keptPriceCents === u.keptPriceCents);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(<>{same.length === l.units.length ? "" : <><span className="mono">{same.map((x) => x.serialNumber).join(", ")}</span>: </>}“{u.priceReason}”{u.keptBy ? ` · ${shortName(u.keptBy.displayName)}` : ""}</>);
  }
  return out;
}

// --- a receipt as posted (UC-PURCHASING-008 step 4) ----------------------------------------------------------------------

/** What a posted receipt warns of (A9, A14), in words: a line no band or markup priced, a delivery note already received. */
export function ReceiptWarnings({ warnings, lines }: { warnings: readonly GoodsReceiptWarning[]; lines: readonly GoodsReceiptLineView[] }) {
  if (warnings.length === 0) return null;
  return (
    <>
      {warnings.map((w) => {
        if (w.code === "duplicate_delivery_note") {
          return <Alert key={`${w.path}-${w.code}`} tone="warn"><b>Delivery note already received.</b> It is also on <ReceiptLink id={w.receiptId} number={w.receiptNumber} />{w.receivedDate ? ` (${dayWords(w.receivedDate)})` : ""} from this supplier; check the delivery was not taken in twice.</Alert>;
        }
        const i = Number(/^lines\.(\d+)/.exec(w.path)?.[1] ?? "-1");
        const l = lines[i];
        return <Alert key={`${w.path}-${w.code}`} tone="warn"><b>No band or markup priced line {i + 1}.</b> {l ? `${l.description} (${l.sku}) at ${formatMoney(l.unitCostCents)}` : "Its cost"} — the price kept was typed or the current price.</Alert>;
      })}
    </>
  );
}

/** The receipt's header and its lines with their serials and prices, exactly as posted; nothing on it changes. */
export function ReceiptBody({ v }: { v: GoodsReceiptView }) {
  const s = useSessionView();
  const serialLinks = can(s, "inventory.serial.read");
  const controlLog = can(s, "governance.control_log.read");
  const units = v.lines.reduce((n, l) => n + l.units.length, 0);
  return (
    <>
      <Panel title="Details" meta={`${v.branch.name} · posted ${formatDateTime(v.postedAt)}`}>
        <KV rows={[
          ["Branch", v.branch.name],
          ["Supplier", <><Link to={`/purchasing/suppliers/${v.supplier.id}`}>{v.supplier.name}</Link>{v.supplier.accountNumber ? <> · account <span className="mono">{v.supplier.accountNumber}</span></> : <span className="faint"> · no account at {v.branch.name}</span>}</>],
          ["Purchase order", v.purchaseOrderId ? <span className="mono">{v.purchaseOrderId}</span> : "No purchase order"],
          ["Reason", v.reason ?? dash],
          ["Delivery note", v.deliveryNoteNumber ? <span className="mono">{v.deliveryNoteNumber}</span> : dash],
          ["Received", <>{dayWords(v.receivedDate)}{v.receivedBy ? ` · ${shortName(v.receivedBy.displayName)}` : ""}</>],
          ["Posted", <>{formatDateTime(v.postedAt)}{v.postedBy ? ` · ${shortName(v.postedBy.displayName)}` : ""}</>],
          ...(v.notes ? [["Notes", v.notes] as [ReactNode, ReactNode]] : []),
        ]} />
      </Panel>
      <Panel title="Lines" meta={`${v.lineCount} line${v.lineCount === 1 ? "" : "s"} · ${v.unitCount} unit${v.unitCount === 1 ? "" : "s"}${units > 0 ? ` · ${units} serial${units === 1 ? "" : "s"}` : ""}`} tight
        foot={<span className="help">A receipt calculates no VAT: the supplier&#39;s invoice becomes a bill from Sprint 7. A posted receipt is never edited — a quantity is put right by a stock adjustment, a serial recorded in error by deleting it. Labels are printed from Sprint 13; each serial&#39;s barcode is its number.</span>}>
        <div className="tbl-scroll">
          <table className="receipt-lines">
            <thead><tr><th className="r">#</th><th>Product</th><th className="r">Qty</th><th className="r">Unit cost</th><th className="r">Line total</th><th>Serials</th><th>Price (incl. VAT)</th></tr></thead>
            <tbody>
              {v.lines.map((l) => {
                const reasons = priceReasons(l);
                const pinned = l.controlLogId !== null || l.units.some((u) => u.controlLogId !== null);
                return (
                  <tr key={l.id}>
                    <td className="r faint num lineno">{l.lineNo}</td>
                    <td className="product">{l.description}<div className="small faint mono">{l.sku}</div></td>
                    <td className="r num" data-label="Qty">{l.quantity}</td>
                    <td className="money" data-label="Unit cost">{formatMoney(l.unitCostCents)}</td>
                    <td className="money" data-label="Line total">{formatMoney(l.lineTotalExVatCents)}</td>
                    <td className="serials" data-label="Serials">
                      {l.units.length === 0 ? <span className="faint">no serials</span> : l.units.map((u) => (
                        <div key={u.id} className="nw">
                          {u.serialVoided
                            ? <><span className="mono">{u.serialNumber}</span> <Chip tone="neutral">Deleted</Chip><div className="small faint wrap">{[u.serialVoidedAt ? formatDate(u.serialVoidedAt) : null, u.serialVoidedBy ? shortName(u.serialVoidedBy.displayName) : null, u.serialVoidReason].filter(Boolean).join(" · ")}</div></>
                            : serialLinks ? <Link className="mono" to={`/stock/serials/${u.serialId}`}>{u.serialNumber}</Link> : <span className="mono">{u.serialNumber}</span>}
                          {u.source === "SupplierSerial" && !u.serialVoided && <span className="small faint"> · supplier&#39;s</span>}
                        </div>
                      ))}
                    </td>
                    <td className="price" data-label="Price (incl. VAT)">
                      {postedPriceWords(l, v.branch.name, v.postedAt)}
                      {reasons.map((r, i) => <div key={i} className="small faint">{r}</div>)}
                      {pinned && <div className="small faint">Control PIN{controlLog ? <> · <ControlLogRef /></> : ""}</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot><tr><td></td><td colSpan={3}>Total ex VAT</td><td className="money strong">{formatMoney(v.totalExVatCents)}</td><td colSpan={2}></td></tr></tfoot>
          </table>
        </div>
      </Panel>
    </>
  );
}

/** `1 line · 2 units`. */
export const countsWords = (lines: number, units: number): string => `${lines} line${lines === 1 ? "" : "s"} · ${units} unit${units === 1 ? "" : "s"}`;

/** A list of things in words: `a, b and c`. */
export const listWords = (items: readonly ReactNode[]): ReactNode => items.map((x, i) => <Fragment key={i}>{i > 0 ? (i === items.length - 1 ? " and " : ", ") : ""}{x}</Fragment>);
